
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

const AnalyticsDebugPanel = () => {
  const { data: eventStats, isLoading: isLoadingEvents, refetch: refetchEvents } = useQuery({
    queryKey: ['analytics-event-stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_activity_events')
        .select('event_category, event_type')
        .order('timestamp', { ascending: false });
      
      if (error) {
        console.error('Error fetching event stats:', error);
        throw error;
      }
      
      // Count all events by category (complete historical data)
      const categoryStats: Record<string, number> = {};
      const typeStats: Record<string, number> = {};
      
      data.forEach(event => {
        categoryStats[event.event_category] = (categoryStats[event.event_category] || 0) + 1;
        typeStats[event.event_type] = (typeStats[event.event_type] || 0) + 1;
      });
      
      return {
        totalEvents: data.length,
        categoryStats,
        typeStats
      };
    },
  });

  const { data: sessionStats, isLoading: isLoadingSessions, refetch: refetchSessions } = useQuery({
    queryKey: ['analytics-session-stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_sessions')
        .select('*')
        .order('started_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching session stats:', error);
        throw error;
      }
      
      const activeSessions = data.filter(s => !s.ended_at);
      const completedSessions = data.filter(s => s.ended_at);
      const avgDuration = completedSessions.length > 0 
        ? completedSessions.reduce((sum, s) => sum + (s.duration_seconds || 0), 0) / completedSessions.length 
        : 0;
      
      return {
        totalSessions: data.length,
        activeSessions: activeSessions.length,
        completedSessions: completedSessions.length,
        avgDurationMinutes: Math.round(avgDuration / 60)
      };
    },
  });

  const handleRefresh = () => {
    refetchEvents();
    refetchSessions();
  };

  if (isLoadingEvents || isLoadingSessions) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Analytics Debug Panel</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse">Loading analytics data...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Analytics Debug Panel - Complete Historical Data</CardTitle>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleRefresh}
          className="ml-2"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Event Statistics */}
        <div>
          <h3 className="text-lg font-semibold mb-3">Event Statistics (All Time)</h3>
          <div className="bg-muted p-4 rounded-lg">
            <p className="font-medium mb-2">Total Events: {eventStats?.totalEvents || 0}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <h4 className="font-medium mb-2">By Category:</h4>
                <div className="space-y-1 text-sm">
                  {eventStats?.categoryStats && Object.entries(eventStats.categoryStats)
                    .sort(([,a], [,b]) => b - a)
                    .map(([category, count]) => (
                    <div key={category} className="flex justify-between">
                      <span>{category}:</span>
                      <span className="font-mono">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="font-medium mb-2">By Type (Top 10):</h4>
                <div className="space-y-1 text-sm">
                  {eventStats?.typeStats && Object.entries(eventStats.typeStats)
                    .sort(([,a], [,b]) => b - a)
                    .slice(0, 10)
                    .map(([type, count]) => (
                    <div key={type} className="flex justify-between">
                      <span className="truncate mr-2">{type}:</span>
                      <span className="font-mono">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Session Statistics */}
        <div>
          <h3 className="text-lg font-semibold mb-3">Session Statistics</h3>
          <div className="bg-muted p-4 rounded-lg">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="font-medium">Total Sessions</p>
                <p className="text-xl font-mono">{sessionStats?.totalSessions || 0}</p>
              </div>
              <div>
                <p className="font-medium">Active Sessions</p>
                <p className="text-xl font-mono">{sessionStats?.activeSessions || 0}</p>
              </div>
              <div>
                <p className="font-medium">Completed Sessions</p>
                <p className="text-xl font-mono">{sessionStats?.completedSessions || 0}</p>
              </div>
              <div>
                <p className="font-medium">Avg Duration</p>
                <p className="text-xl font-mono">{sessionStats?.avgDurationMinutes || 0}m</p>
              </div>
            </div>
          </div>
        </div>

        {/* Activity Categories Reference */}
        <div>
          <h3 className="text-lg font-semibold mb-3">Activity Categories Reference</h3>
          <div className="bg-muted p-4 rounded-lg text-sm space-y-2">
            <div><strong>content_saving:</strong> Manual saves, extension saves, imports</div>
            <div><strong>organization:</strong> Collections created/edited, tags created/edited, content organized</div>
            <div><strong>curation:</strong> Content editing, note taking, manual thumbnail updates</div>
            <div><strong>discovery:</strong> Navigation (collections, tags, boards), searches, content clicks</div>
            <div><strong>system:</strong> App opens, authentication, system events</div>
            <div><strong>import_process:</strong> Bookmark imports and related events</div>
            <div><strong>user_action:</strong> General user interactions and feature usage</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AnalyticsDebugPanel;
